import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSpinner,
  faTrash,
  faEdit,
  faCalendarAlt,
  faRepeat,
  faBullhorn,
  faTimes,
  faUsers,
  faEye,
  faShareAlt,
  faMapMarkerAlt,
  faPlayCircle,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { collection, doc, getDoc, onSnapshot } from "firebase/firestore";
import { firestore } from "../../FirebaseConfig";
import { EVENT_STATUS_KEYS } from "../../constants/events";
import { describeRecurrence } from "../../utils/recurrence";
import { useToast } from "../ui/Toast";
import { ErrorMessage } from "../ui/ErrorMessage";
import { cancelEvent, listEvents } from "../../utils/events";
import { groupStacks, tsToMs, formatCountdown } from "../../utils/promotionStacks";
import EventEditorWizard from "./EventEditorWizard";
import PromotionStackPanel from "./PromotionStackPanel";
import EventRsvpModal from "./EventRsvpModal";
import "../../styles/EventsManager.css";

/**
 * EventsManager — business-side events surface for a single listing.
 *
 * Renders the listing's events plus an `EventEditorWizard` for create/edit.
 * The wizard handles templates, recurrence, ticketing and grace windows.
 * Auto-publishes when required fields validate; otherwise saves as draft.
 * Admins are notified by the server but do NOT need to approve events.
 */

function formatStartLabel(ev) {
  const ts = ev.nextStartAt
    || (ev.startAt && (ev.startAt.toMillis ? ev.startAt.toMillis() : new Date(ev.startAt).getTime()))
    || null;
  if (!ts) return "Unscheduled";
  const d = new Date(ts);
  return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

export default function EventsManager({
  businessId,
  listing = null,
  userListings = [],
  prefill = null,
  onPrefillConsumed,
}) {
  const { showToast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | { mode, event }
  const [promoting, setPromoting] = useState(null); // null | event being promoted
  const [viewingRsvps, setViewingRsvps] = useState(null); // null | event
  const [analyticsById, setAnalyticsById] = useState({}); // { [eventId]: { views, shareClicks, directionsClicks } }

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await listEvents({
        businessId,
        statusFilter: null,
        upcomingOnly: false,
        limit: 100,
      });
      setEvents(items);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (businessId) refresh();
  }, [businessId, refresh]);

  // Fetch per-event analytics rollups once events load. Best-effort: failures
  // are silent so missing analytics docs never break the manager UI.
  useEffect(() => {
    if (!events || events.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          events.map(async (ev) => {
            try {
              const snap = await getDoc(doc(firestore, "eventAnalytics", ev.id));
              return [ev.id, snap.exists() ? snap.data() : null];
            } catch {
              return [ev.id, null];
            }
          }),
        );
        if (cancelled) return;
        const next = {};
        results.forEach(([id, data]) => { next[id] = data || {}; });
        setAnalyticsById(next);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [events]);

  // Consume parent prefill (the "Add Event" button on a listing card).
  useEffect(() => {
    if (prefill?.mode === "create") {
      setEditing({ mode: "create", event: null });
      onPrefillConsumed?.();
    }
  }, [prefill, onPrefillConsumed]);

  const startNew = () => setEditing({ mode: "create", event: null });
  const startEdit = (ev) => setEditing({ mode: "edit", event: ev });
  const closeEditor = () => setEditing(null);

  const handleSaved = async () => {
    setEditing(null);
    await refresh();
  };

  const handleCancelEvent = async (ev) => {
    if (!window.confirm(`Cancel event "${ev.title}"? Travelers who RSVP'd will be notified.`)) return;
    try {
      await cancelEvent(ev.id);
      showToast("Event cancelled.", "success");
      await refresh();
    } catch (err) {
      showToast(err?.message || "Couldn't cancel the event.", "error");
    }
  };

  if (editing) {
    return (
      <EventEditorWizard
        businessId={businessId}
        listing={listing}
        userListings={userListings}
        initialEvent={editing.event}
        onSaved={handleSaved}
        onCancel={closeEditor}
      />
    );
  }

  return (
    <div className="events-manager">
      <div className="events-manager__header">
        <h2>
          <FontAwesomeIcon icon={faCalendarAlt} /> Events
        </h2>
        <button type="button" className="btn-primary" onClick={startNew}>
          <FontAwesomeIcon icon={faPlus} /> New Event
        </button>
      </div>

      {error && <ErrorMessage error={error} title="Couldn't load events" />}

      {loading ? (
        <div className="events-manager__loading">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading events…
        </div>
      ) : events.length === 0 ? (
        <div className="events-manager__empty">
          <p>No events yet for this listing. Create your first one to attract Guild members.</p>
        </div>
      ) : (
        <ul className="events-manager__list">
          {events.map((ev) => {
            const ruleSummary = describeRecurrence(ev.recurrence);
            return (
              <li
                key={ev.id}
                className={`events-manager__item events-manager__item--${ev.status}`}
              >
                <div className="events-manager__item-main">
                  <div className="events-manager__item-title">{ev.title}</div>
                  <div className="events-manager__item-meta">
                    <span className={`events-manager__status events-manager__status--${ev.status}`}>
                      {ev.status}
                    </span>
                    <span>{formatStartLabel(ev)}</span>
                    {(ev.ticketPriceCents || 0) > 0 ? (
                      <span>${(ev.ticketPriceCents / 100).toFixed(2)}</span>
                    ) : (
                      <span>Free</span>
                    )}
                    {ev.capacity && (
                      <span>{ev.rsvpCount || 0} / {ev.capacity} reserved</span>
                    )}
                    {ruleSummary && (
                      <span title={ruleSummary}>
                        <FontAwesomeIcon icon={faRepeat} /> {ruleSummary}
                      </span>
                    )}
                    {ev.removedByAdmin && (
                      <span className="events-manager__status events-manager__status--cancelled">
                        Removed by admin
                      </span>
                    )}
                  </div>
                  {(() => {
                    const a = analyticsById[ev.id] || {};
                    return (
                      <div className="events-manager__metrics" aria-label="Event metrics">
                        <span className="events-manager__metric-chip" title="Page views">
                          <FontAwesomeIcon icon={faEye} /> {a.views || 0} views
                        </span>
                        <span className="events-manager__metric-chip" title="RSVPs">
                          <FontAwesomeIcon icon={faUsers} /> {ev.rsvpCount || 0} RSVPs
                        </span>
                        <span className="events-manager__metric-chip" title="Share clicks">
                          <FontAwesomeIcon icon={faShareAlt} /> {a.shareClicks || 0} shares
                        </span>
                        <span className="events-manager__metric-chip" title="Directions clicks">
                          <FontAwesomeIcon icon={faMapMarkerAlt} /> {a.directionsClicks || 0} directions
                        </span>
                      </div>
                    );
                  })()}
                  <EventPromotionChips
                    eventId={ev.id}
                    onPromote={() => setPromoting(ev)}
                  />
                </div>
                <div className="events-manager__item-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => startEdit(ev)}
                    disabled={ev.status === EVENT_STATUS_KEYS.CANCELLED}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setViewingRsvps(ev)}
                    disabled={ev.status === EVENT_STATUS_KEYS.DRAFT}
                    title="See who RSVP'd to this event"
                  >
                    <FontAwesomeIcon icon={faUsers} /> View RSVPs ({ev.rsvpCount || 0})
                  </button>
                  <button
                    type="button"
                    className="btn-promote"
                    onClick={() => setPromoting(ev)}
                    disabled={ev.status === EVENT_STATUS_KEYS.CANCELLED || ev.status === EVENT_STATUS_KEYS.COMPLETED}
                    title="Buy promotion stacks for this event"
                  >
                    <FontAwesomeIcon icon={faBullhorn} /> Promote
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={() => handleCancelEvent(ev)}
                    disabled={
                      ev.status === EVENT_STATUS_KEYS.CANCELLED
                      || ev.status === EVENT_STATUS_KEYS.COMPLETED
                    }
                  >
                    <FontAwesomeIcon icon={faTrash} /> Cancel
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {promoting && (
        <div
          className="events-manager__promote-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Promote event ${promoting.title}`}
          onClick={() => setPromoting(null)}
        >
          <div
            className="events-manager__promote-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="events-manager__promote-modal-header">
              <div>
                <span className="events-manager__promote-eyebrow">Promote event</span>
                <h3 className="events-manager__promote-title">{promoting.title}</h3>
              </div>
              <button
                type="button"
                className="events-manager__promote-close"
                onClick={() => setPromoting(null)}
                aria-label="Close promote dialog"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <PromotionStackPanel
              targetType="event"
              targetId={promoting.id}
              target={promoting}
            />
          </div>
        </div>
      )}

      {viewingRsvps && (
        <EventRsvpModal event={viewingRsvps} onClose={() => setViewingRsvps(null)} />
      )}
    </div>
  );
}

/**
 * EventPromotionChips
 * Live strip showing the event's currently-active and upcoming promotion
 * stacks. Each chip is clickable and opens the Promote modal via `onPromote`.
 * Hidden when no active or scheduled stacks exist.
 */
function EventPromotionChips({ eventId, onPromote }) {
  const [stacks, setStacks] = useState([]);
  const [, setNowTick] = useState(0);

  useEffect(() => {
    if (!eventId) return undefined;
    const ref = collection(firestore, "events", eventId, "promotionStacks");
    const unsub = onSnapshot(
      ref,
      (snap) => setStacks(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setStacks([])
    );
    return () => unsub();
  }, [eventId]);

  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const { active, scheduled } = groupStacks(stacks);
  if (active.length === 0 && scheduled.length === 0) return null;

  const labelFor = (s) => {
    if (s.type === "custom") return "Custom range";
    // Capitalize tier key as a fallback label.
    return s.type ? s.type.charAt(0).toUpperCase() + s.type.slice(1) : "Promotion";
  };

  return (
    <div className="events-manager__promotions" aria-label="Event promotions">
      {active.map((s) => {
        const ms = tsToMs(s.endAt);
        return (
          <button
            type="button"
            key={`a-${s.id}`}
            className="events-manager__promotion-chip events-manager__promotion-chip--active"
            onClick={onPromote}
            title="Manage promotions for this event"
          >
            <span className="events-manager__promotion-chip-eyebrow">
              <FontAwesomeIcon icon={faPlayCircle} /> Active
            </span>
            <span className="events-manager__promotion-chip-label">{labelFor(s)}</span>
            <span className="events-manager__promotion-chip-meta">
              <FontAwesomeIcon icon={faClock} /> ends in {formatCountdown(ms - Date.now())}
            </span>
          </button>
        );
      })}
      {scheduled.map((s) => {
        const ms = tsToMs(s.startAt);
        return (
          <button
            type="button"
            key={`s-${s.id}`}
            className="events-manager__promotion-chip events-manager__promotion-chip--scheduled"
            onClick={onPromote}
            title="Manage promotions for this event"
          >
            <span className="events-manager__promotion-chip-eyebrow">
              <FontAwesomeIcon icon={faCalendarAlt} /> Scheduled
            </span>
            <span className="events-manager__promotion-chip-label">{labelFor(s)}</span>
            <span className="events-manager__promotion-chip-meta">
              <FontAwesomeIcon icon={faClock} /> starts in {formatCountdown(ms - Date.now())}
            </span>
          </button>
        );
      })}
    </div>
  );
}
